import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Skeleton,
  useTheme
} from '@mui/material';
import { calculateProgressPercentage } from '../../utils/progressCalculations';
import { ProgressDisplay } from './ProgressDisplay';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Data structure for individual team summary information
 */
export interface TeamSummaryData {
  /** Team name (displayed in chip format) */
  name: string;
  /** Total story points assigned to the team */
  total: number;
  /** Completed story points by the team */
  completed: number;
  /** Optional pre-calculated progress percentage (0-100). If not provided, will be calculated from completed/total */
  progress?: number;
}

/**
 * Props for the TeamSummaryTable component
 */
export interface TeamSummaryTableProps {
  /** Array of team summary data to display */
  teamData: TeamSummaryData[];
  /** Whether to show the progress column with bars and percentages (default: true) */
  showProgress?: boolean;
  /** Callback function when a team row is clicked. Makes rows clickable with pointer cursor */
  onTeamClick?: (teamName: string) => void;
  /** Whether to show loading skeleton animations instead of data (default: false) */
  loading?: boolean;
  /** Optional title for the table (not currently displayed in UI) */
  title?: string;
  /** Message to display when teamData array is empty (default: "No team data available") */
  emptyMessage?: string;
  /** Material-UI Paper variant for table container (default: "outlined") */
  variant?: 'outlined' | 'elevation';
  /** Table size affecting minimum width and spacing (default: "medium") */
  size?: 'small' | 'medium';
}

/**
 * Props for individual team row component
 */
interface TeamSummaryRowProps {
  /** Team data to display in the row */
  team: TeamSummaryData;
  /** Whether to show progress column */
  showProgress: boolean;
  /** Optional click handler for the row */
  onClick?: (teamName: string) => void;
}

/**
 * Props for table header component
 */
interface TeamSummaryTableHeaderProps {
  /** Whether to show progress column header */
  showProgress: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================



// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Table header component for team summary table
 */
const TeamSummaryTableHeader: React.FC<TeamSummaryTableHeaderProps> = ({ showProgress }) => {
  const theme = useTheme();
  
  return (
    <TableHead>
      <TableRow sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
      }}>
        <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
          Team
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', width: '20%' }}>
          Total Points
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', width: '20%' }}>
          Completed Points
        </TableCell>
        {showProgress && (
          <TableCell align="right" sx={{ fontWeight: 'bold', width: 200 }}>
            Progress
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};

/**
 * Individual team row component
 */
const TeamSummaryRow: React.FC<TeamSummaryRowProps> = ({ team, showProgress, onClick }) => {
  const theme = useTheme();
  const progress = team.progress ?? calculateProgressPercentage(team.completed, team.total);
  
  const handleClick = () => {
    if (onClick) {
      onClick(team.name);
    }
  };

  return (
    <TableRow 
      key={team.name}
      onClick={handleClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
        }
      }}
    >
      <TableCell sx={{ py: 2 }}>
        <Chip
          label={team.name}
          variant="outlined"
          size="small"
          sx={{
            maxWidth: '100%',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: 2, fontWeight: 'medium' }}>
        {team.total}
      </TableCell>
      <TableCell align="right" sx={{ py: 2, fontWeight: 'medium' }}>
        {team.completed}
      </TableCell>
      {showProgress && (
        <TableCell align="right" sx={{ py: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5
          }}>
            <Box sx={{
              width: 120,
              flexShrink: 0,
              lineHeight: 0,
              fontSize: 0,
              display: 'block'
            }}>
              <ProgressDisplay
                percentage={progress}
                variant="bar"
                size="small"
                showPercentage={false}
              />
            </Box>
            <Box sx={{ width: 55, flexShrink: 0, textAlign: 'right' }}>
              <ProgressDisplay
                percentage={progress}
                variant="text"
                size="medium"
                precision={1}
              />
            </Box>
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

/**
 * Loading skeleton for the table
 */
const TableSkeleton: React.FC<{ showProgress: boolean; rows?: number }> = ({ 
  showProgress, 
  rows = 3 
}) => {
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index}>
          <TableCell sx={{ py: 2 }}>
            <Skeleton variant="rectangular" width={120} height={24} sx={{ borderRadius: 12 }} />
          </TableCell>
          <TableCell align="right" sx={{ py: 2 }}>
            <Skeleton variant="text" width={40} />
          </TableCell>
          <TableCell align="right" sx={{ py: 2 }}>
            <Skeleton variant="text" width={40} />
          </TableCell>
          {showProgress && (
            <TableCell align="right" sx={{ py: 2 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                minWidth: 140
              }}>
                <Skeleton variant="rectangular" width={80} height={6} sx={{ borderRadius: 3 }} />
                <Skeleton variant="text" width={45} />
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Reusable team summary table component
 *
 * Displays team story points data in a structured table format with optional
 * progress indicators, click handlers, and loading states. Extracted from
 * InitiativeDetails.tsx to promote reusability across dashboard views.
 *
 * @example
 * ```tsx
 * const teamData = [
 *   { name: 'Frontend Team', total: 45, completed: 32 },
 *   { name: 'Backend Team', total: 38, completed: 15 }
 * ];
 *
 * <TeamSummaryTable
 *   teamData={teamData}
 *   loading={false}
 *   showProgress={true}
 *   onTeamClick={(team) => navigate(`/teams/${team}`)}
 * />
 * ```
 *
 * @param props - Component props
 * @returns JSX element containing the team summary table
 */
export const TeamSummaryTable: React.FC<TeamSummaryTableProps> = ({
  teamData,
  showProgress = true,
  onTeamClick,
  loading = false,
  title,
  emptyMessage = "No team data available",
  variant = "outlined",
  size = "medium"
}) => {
  const tableMinWidth = size === 'small' ? 500 : 650;
  const colSpan = showProgress ? 4 : 3;

  if (loading) {
    return (
      <TableContainer component={Paper} variant={variant}>
        <Table sx={{ minWidth: tableMinWidth }}>
          <TeamSummaryTableHeader showProgress={showProgress} />
          <TableSkeleton showProgress={showProgress} />
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} variant={variant}>
      <Table sx={{ minWidth: tableMinWidth }}>
        <TeamSummaryTableHeader showProgress={showProgress} />
        <TableBody>
          {teamData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            teamData.map((team) => (
              <TeamSummaryRow 
                key={team.name}
                team={team}
                showProgress={showProgress}
                onClick={onTeamClick}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TeamSummaryTable;
